import { formatInTimeZone } from 'date-fns-tz';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    OUTREACH_ANALYTICS_TIMEZONE,
    type OutreachUrgencyFilter,
} from '@apps/web/components/admin/outreach_observability/constants';
import { RunsDrillDialog } from '@apps/web/components/admin/outreach_observability/runs_drill_dialog';
import { type HourlyKpiBucket } from '@libraries/typescript/api_client';

// The metrics that carry an hourly sparkline. This is a strict subset of the
// full KpiRunMetric union (the backend widened it to 15 drillable metrics, but
// only these five have a per-tile trend line), so KPI_METRICS below stays a
// small, exhaustively-typed map instead of needing an entry per backend metric.
// Every member here is also a valid KpiRunMetric, so a sparkline key drops
// straight into the runs drill-down.
export type SparklineMetricKey =
    | 'runs'
    | 'runs_cancelled'
    | 'shifts_yes'
    | 'compaction'
    | 'stale';

type KpiMetricConfig = {
    readonly label: string;
    readonly kind: 'rate' | 'count';
    // Per-bucket plotted value. Rates return null when the denominator is 0 so
    // the line breaks rather than dropping to a misleading 0%.
    readonly value: (bucket: HourlyKpiBucket) => number | null;
    readonly numerator: (bucket: HourlyKpiBucket) => number;
    readonly denominator?: (bucket: HourlyKpiBucket) => number;
    // Color hint: alert-ish tiles trend in the destructive color.
    readonly destructive?: boolean;
};

function ratio(numerator: number, denominator: number): number | null {
    return denominator === 0 ? null : numerator / denominator;
}

export const KPI_METRICS: Record<SparklineMetricKey, KpiMetricConfig> = {
    runs: {
        label: 'Runs',
        kind: 'count',
        value: (bucket) => bucket.num_runs,
        numerator: (bucket) => bucket.num_runs,
    },
    runs_cancelled: {
        label: '% runs cancelled',
        kind: 'rate',
        value: (bucket) => ratio(bucket.cancelled_run_count, bucket.num_runs),
        numerator: (bucket) => bucket.cancelled_run_count,
        denominator: (bucket) => bucket.num_runs,
        destructive: true,
    },
    shifts_yes: {
        label: '% shifts yes',
        kind: 'rate',
        value: (bucket) =>
            ratio(bucket.yes_shift_count, bucket.covered_shift_count),
        numerator: (bucket) => bucket.yes_shift_count,
        denominator: (bucket) => bucket.covered_shift_count,
    },
    compaction: {
        label: 'Compaction runs',
        kind: 'count',
        value: (bucket) => bucket.compaction_run_count,
        numerator: (bucket) => bucket.compaction_run_count,
    },
    stale: {
        label: 'Stale / abandoned',
        kind: 'count',
        value: (bucket) => bucket.stale_idle_count,
        numerator: (bucket) => bucket.stale_idle_count,
        destructive: true,
    },
};

function formatMetricValue(
    kind: 'rate' | 'count',
    value: number | null,
): string {
    if (value === null) {
        return '—';
    }
    return kind === 'rate'
        ? `${(value * 100).toFixed(1)}%`
        : String(Math.round(value));
}

function metricColor(config: KpiMetricConfig): string {
    return config.destructive ? 'var(--destructive)' : 'var(--chart-1)';
}

export function KpiSparkline({
    buckets,
    metricKey,
}: {
    readonly buckets: Array<HourlyKpiBucket>;
    readonly metricKey: SparklineMetricKey;
}) {
    const config = KPI_METRICS[metricKey];
    if (buckets.length === 0) {
        return null;
    }
    const data = buckets.map((bucket) => toTrendPoint(bucket, config));
    return (
        <div className="h-24 w-full">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={data}
                    margin={{ top: 4, right: 8, bottom: 2, left: 0 }}
                >
                    <XAxis
                        dataKey="label"
                        tick={{ fontSize: 9 }}
                        tickLine={false}
                        axisLine={false}
                        interval="preserveStartEnd"
                        minTickGap={40}
                        height={18}
                        tickMargin={6}
                    />
                    <YAxis
                        tick={{ fontSize: 9 }}
                        tickLine={false}
                        axisLine={false}
                        width={28}
                        tickCount={3}
                        tickFormatter={(value: number) =>
                            formatAxisTick(config, value)
                        }
                    />
                    <RechartsTooltip
                        content={<KpiTrendTooltip config={config} />}
                    />
                    <Line
                        type="monotone"
                        dataKey="value"
                        stroke={metricColor(config)}
                        dot={false}
                        strokeWidth={1.5}
                        connectNulls={false}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

type TrendPoint = {
    readonly label: string;
    readonly value: number | null;
    readonly numerator: number;
    readonly denominator: number | null;
};

function toTrendPoint(
    bucket: HourlyKpiBucket,
    config: KpiMetricConfig,
): TrendPoint {
    return {
        label: formatInTimeZone(
            new Date(bucket.bucket_start),
            OUTREACH_ANALYTICS_TIMEZONE,
            'M/d ha',
        ),
        value: config.value(bucket),
        numerator: config.numerator(bucket),
        denominator: config.denominator ? config.denominator(bucket) : null,
    };
}

function formatAxisTick(config: KpiMetricConfig, value: number): string {
    return config.kind === 'rate'
        ? `${(value * 100).toFixed(0)}%`
        : String(value);
}

function KpiTrendTooltip({
    active,
    payload,
    config,
}: {
    readonly active?: boolean;
    readonly payload?: Array<{ readonly payload?: TrendPoint }>;
    readonly config: KpiMetricConfig;
}) {
    const point = payload?.[0]?.payload;
    if (!active || !point) {
        return null;
    }
    return (
        <div className="bg-background rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="mb-1 font-medium">{point.label}</div>
            <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">{config.label}</span>
                <span className="tabular-nums">
                    {formatMetricValue(config.kind, point.value)}
                </span>
            </div>
            {config.kind === 'rate' && point.denominator !== null ? (
                <div className="text-muted-foreground mt-1 tabular-nums">
                    {point.numerator}/{point.denominator}
                </div>
            ) : null}
        </div>
    );
}

// Thin wrapper over RunsDrillDialog for a sparklined tile: renders the full
// 48h trend chart into the dialog's chart slot and lets RunsDrillDialog own the
// fetch + runs table, so the chart+table drill-down behaves exactly as before.
export function KpiTrendDialog({
    open,
    onOpenChange,
    buckets,
    metricKey,
    organizationIds,
    urgency,
}: {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly buckets: Array<HourlyKpiBucket>;
    readonly metricKey: SparklineMetricKey;
    readonly organizationIds: Array<string>;
    readonly urgency: OutreachUrgencyFilter;
}) {
    const config = KPI_METRICS[metricKey];
    const data = buckets.map((bucket) => toTrendPoint(bucket, config));
    return (
        <RunsDrillDialog
            open={open}
            onOpenChange={onOpenChange}
            title={config.label}
            description="Hourly, last 48 hours (America/New_York). Live mode."
            metric={metricKey}
            organizationIds={organizationIds}
            urgency={urgency}
            sparkline={
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data} margin={{ left: 8, right: 8 }}>
                        <CartesianGrid
                            stroke="var(--border)"
                            strokeDasharray="3 3"
                        />
                        <XAxis
                            dataKey="label"
                            tick={{ fontSize: 11 }}
                            minTickGap={28}
                        />
                        <YAxis
                            tick={{ fontSize: 11 }}
                            width={44}
                            allowDecimals={config.kind === 'rate'}
                            tickFormatter={(value: number) =>
                                formatAxisTick(config, value)
                            }
                        />
                        <RechartsTooltip
                            content={<KpiTrendTooltip config={config} />}
                        />
                        <Line
                            type="monotone"
                            dataKey="value"
                            name={config.label}
                            stroke={metricColor(config)}
                            dot={false}
                            strokeWidth={2}
                            connectNulls={false}
                            isAnimationActive={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            }
        />
    );
}
